/**
 * ROI Calculator Engine
 * Calculates ROSI (Return on Security Investment) for cybersecurity investments
 */

import {
  SecurityInvestment,
  RiskProfile,
  ROSIResult,
  YearProjection,
  SensitivityScenario,
  ThreatCoverageResult,
  ComparisonResult,
  InvestmentRanking,
  CombinedInvestmentAnalysis,
  ComplianceFramework,
  ThreatType,
  SecurityMaturityLevel
} from '@/types/calculator';

import {
  LOADED_HOURLY_RATE,
  DISCOUNT_RATE,
  COMPLIANCE_VALUES
} from './roi-constants';

// ============================================================================
// MAIN ROSI CALCULATION
// ============================================================================

export function calculateROSI(
  investment: SecurityInvestment,
  riskProfile: RiskProfile
): ROSIResult {
  // Step 1: Calculate investment costs
  const costs = calculateInvestmentCost(investment);

  // Step 2: Calculate risk reduction value
  const riskReduction = calculateRiskReduction(
    riskProfile.ALE,
    investment.riskMitigationPercent,
    investment.timeToEffectiveness
  );

  // Step 3: Calculate additional benefits
  const additionalBenefits = calculateAdditionalBenefits(investment);

  // Step 4: Calculate Year 1 ROSI
  const year1Results = calculateYear1ROSI(
    costs,
    riskReduction,
    additionalBenefits
  );

  // Step 5: Multi-year projection
  const multiYearProjection = calculateMultiYearROI(
    investment,
    riskProfile.ALE,
    costs,
    riskReduction,
    additionalBenefits
  );

  // Step 6: Sensitivity analysis
  const sensitivityAnalysis = calculateSensitivityAnalysis(
    investment,
    riskProfile.ALE,
    costs,
    additionalBenefits
  );

  // Step 7: Threat coverage
  const threatCoverage = analyzeThreatCoverage(
    investment.threatsAddressed,
    riskProfile.currentMaturityLevel
  );

  // Step 8: Generate executive summary
  const executiveSummary = generateExecutiveSummary(
    investment,
    year1Results,
    multiYearProjection
  );

  // Step 9: Recommendation
  const recommendation = determineRecommendation(
    year1Results.rosiYear1,
    year1Results.paybackPeriod,
    multiYearProjection
  );

  return {
    investmentName: investment.name,
    rosiYear1: year1Results.rosiYear1,
    rosi5Year: multiYearProjection[4].roi,
    paybackPeriod: year1Results.paybackPeriod,
    netBenefitYear1: year1Results.netBenefit,
    npv5Year: multiYearProjection[4].npv,
    costs,
    benefits: {
      riskReductionAnnual: riskReduction.annual,
      riskReductionYear1: riskReduction.year1,
      additionalBenefits,
      totalBenefitYear1: riskReduction.year1 + additionalBenefits
    },
    multiYearProjection,
    sensitivityAnalysis,
    threatCoverage,
    executiveSummary,
    recommendation
  };
}

// ============================================================================
// INVESTMENT COST CALCULATION
// ============================================================================

function calculateInvestmentCost(investment: SecurityInvestment) {
  const oneTime = investment.implementationCost || 0;
  const annualLicensing = investment.annualRecurringCost || 0;

  const personnelCost = (investment.personnelHours || 0) * LOADED_HOURLY_RATE;
  const annual = annualLicensing + personnelCost;
  const year1Total = oneTime + annual;

  return {
    oneTime,
    annual,
    year1Total,
    personnelCost
  };
}

// ============================================================================
// RISK REDUCTION CALCULATION
// ============================================================================

function calculateRiskReduction(
  ALE: number,
  mitigationPercent: number,
  timeToEffectiveness: string
) {
  const annualRiskReduction = ALE * (mitigationPercent / 100);

  // Adjust for time to full effectiveness
  const effectivenessMultipliers: Record<string, number> = {
    immediate: 1.0,
    short: 0.75,
    medium: 0.50,
    long: 0.33
  };

  const year1Multiplier = effectivenessMultipliers[timeToEffectiveness] || 1.0;
  const year1RiskReduction = annualRiskReduction * year1Multiplier;

  return {
    annual: annualRiskReduction,
    year1: year1RiskReduction
  };
}

// ============================================================================
// ADDITIONAL BENEFITS
// ============================================================================

function calculateAdditionalBenefits(
  investment: SecurityInvestment
): number {
  let total = 0;

  // Compliance benefits
  investment.complianceBenefits?.forEach(framework => {
    total += COMPLIANCE_VALUES[framework as ComplianceFramework] || 0;
  });

  // Productivity improvements
  total += investment.productivitySavings || 0;

  // Insurance premium reduction
  if (investment.insurancePremiumReduction) {
    const estimatedPremium = 50000; // Conservative estimate
    total += estimatedPremium * (investment.insurancePremiumReduction / 100);
  }

  // Avoided audit costs
  total += investment.avoidedAuditCosts || 0;

  return total;
}

// ============================================================================
// YEAR 1 ROSI
// ============================================================================

function calculateYear1ROSI(
  costs: ReturnType<typeof calculateInvestmentCost>,
  riskReduction: ReturnType<typeof calculateRiskReduction>,
  additionalBenefits: number
) {
  const totalBenefit = riskReduction.year1 + additionalBenefits;
  const netBenefit = totalBenefit - costs.year1Total;
  const rosiYear1 = (netBenefit / costs.year1Total) * 100;

  // Payback period
  let paybackPeriod: number | null = null;
  if (netBenefit > 0) {
    const annualNetBenefit = riskReduction.annual + additionalBenefits - costs.annual;
    if (annualNetBenefit > 0) {
      paybackPeriod = costs.oneTime / annualNetBenefit;
      // If Year 1 already positive, recalculate
      if (netBenefit >= 0) {
        paybackPeriod = costs.year1Total / totalBenefit;
      }
    }
  }

  return {
    rosiYear1,
    netBenefit,
    paybackPeriod
  };
}

// ============================================================================
// MULTI-YEAR PROJECTION
// ============================================================================

function calculateMultiYearROI(
  investment: SecurityInvestment,
  ALE: number,
  costs: ReturnType<typeof calculateInvestmentCost>,
  riskReduction: ReturnType<typeof calculateRiskReduction>,
  additionalBenefits: number
): YearProjection[] {
  const projections: YearProjection[] = [];
  let cumulativeCost = 0;
  let cumulativeBenefit = 0;

  for (let year = 1; year <= 5; year++) {
    // Costs for this year
    let yearCost = costs.annual;
    if (year === 1) {
      yearCost += costs.oneTime;
    }

    // Benefits for this year
    let yearRiskReduction = riskReduction.annual;

    // Adjust for ramp-up period
    if (investment.timeToEffectiveness === 'short' && year === 1) {
      yearRiskReduction = riskReduction.year1;
    } else if (investment.timeToEffectiveness === 'medium') {
      if (year === 1) yearRiskReduction = riskReduction.annual * 0.50;
      else if (year === 2) yearRiskReduction = riskReduction.annual * 0.75;
    } else if (investment.timeToEffectiveness === 'long') {
      if (year === 1) yearRiskReduction = riskReduction.annual * 0.33;
      else if (year === 2) yearRiskReduction = riskReduction.annual * 0.67;
    }

    const yearBenefit = yearRiskReduction + additionalBenefits;

    // Present value calculations
    const pvCost = yearCost / Math.pow(1 + DISCOUNT_RATE, year);
    const pvBenefit = yearBenefit / Math.pow(1 + DISCOUNT_RATE, year);

    cumulativeCost += pvCost;
    cumulativeBenefit += pvBenefit;

    const yearROI = ((cumulativeBenefit - cumulativeCost) / cumulativeCost) * 100;
    const yearNPV = cumulativeBenefit - cumulativeCost;

    projections.push({
      year,
      cost: yearCost,
      benefit: yearBenefit,
      netBenefit: yearBenefit - yearCost,
      roi: yearROI,
      npv: yearNPV,
      cumulativeCost,
      cumulativeBenefit
    });
  }

  return projections;
}

// ============================================================================
// SENSITIVITY ANALYSIS
// ============================================================================

function calculateSensitivityAnalysis(
  investment: SecurityInvestment,
  ALE: number,
  costs: ReturnType<typeof calculateInvestmentCost>,
  additionalBenefits: number
): SensitivityScenario[] {
  const scenarios: SensitivityScenario[] = [];

  const scenarioDefinitions = [
    { name: 'optimistic', multiplier: 1.25, probability: 20 },
    { name: 'expected', multiplier: 1.0, probability: 50 },
    { name: 'conservative', multiplier: 0.75, probability: 25 },
    { name: 'pessimistic', multiplier: 0.50, probability: 5 }
  ];

  scenarioDefinitions.forEach(({ name, multiplier, probability }) => {
    const adjustedMitigation = investment.riskMitigationPercent * multiplier;
    const riskReduction = calculateRiskReduction(
      ALE,
      adjustedMitigation,
      investment.timeToEffectiveness
    );

    const totalBenefit = riskReduction.year1 + additionalBenefits;
    const netBenefit = totalBenefit - costs.year1Total;
    const rosi = (netBenefit / costs.year1Total) * 100;

    scenarios.push({
      scenario: name as 'optimistic' | 'expected' | 'conservative' | 'pessimistic',
      riskReduction: adjustedMitigation,
      rosi,
      netBenefit,
      probability
    });
  });

  return scenarios;
}

// ============================================================================
// THREAT COVERAGE ANALYSIS
// ============================================================================

function analyzeThreatCoverage(
  threatsAddressed: ThreatType[],
  currentMaturity: SecurityMaturityLevel
): ThreatCoverageResult[] {
  // Simplified threat coverage - can be enhanced with more detailed logic
  const results: ThreatCoverageResult[] = [];

  threatsAddressed.forEach(threat => {
    results.push({
      threat,
      currentRisk: currentMaturity === 'minimal' ? 'high' : 'medium',
      postInvestmentRisk: 'low',
      reductionPercent: 60
    });
  });

  return results;
}

// ============================================================================
// EXECUTIVE SUMMARY GENERATION
// ============================================================================

function generateExecutiveSummary(
  investment: SecurityInvestment,
  year1: ReturnType<typeof calculateYear1ROSI>,
  multiYear: YearProjection[]
): string {
  const year5NPV = multiYear[4].npv;
  const payback = year1.paybackPeriod
    ? `${year1.paybackPeriod.toFixed(1)} years`
    : 'Never';

  let summary = `## Executive Summary: ${investment.name}\n\n`;

  if (year1.rosiYear1 > 50) {
    summary += `### Recommendation: **APPROVE**\n\nThis investment delivers exceptional ROI with ${payback} payback.\n\n`;
  } else if (year1.rosiYear1 > 0) {
    summary += `### Recommendation: **CONSIDER**\n\nThis investment provides positive ROI worth evaluating.\n\n`;
  } else {
    summary += `### Recommendation: **DEFER**\n\nConsider higher-ROI investments first or re-evaluate requirements.\n\n`;
  }

  summary += `### Financial Analysis\n`;
  summary += `- **Year 1 ROI**: ${year1.rosiYear1.toFixed(0)}% ($${year1.netBenefit.toLocaleString()} net benefit)\n`;
  summary += `- **5-Year NPV**: $${year5NPV.toLocaleString()}\n`;
  summary += `- **Payback Period**: ${payback}\n\n`;

  summary += `This investment ${year1.rosiYear1 > 100 ? 'significantly' : ''} reduces annual expected loss while providing ${year1.rosiYear1 > 0 ? 'positive' : 'negative'} financial returns.`;

  return summary;
}

// ============================================================================
// RECOMMENDATION LOGIC
// ============================================================================

function determineRecommendation(
  rosiYear1: number,
  paybackPeriod: number | null,
  multiYear: YearProjection[]
): 'APPROVE' | 'CONSIDER' | 'DEFER' | 'REJECT' {
  const year5NPV = multiYear[4].npv;

  if (rosiYear1 > 100 && paybackPeriod && paybackPeriod < 1.5) {
    return 'APPROVE'; // Excellent ROI
  } else if (rosiYear1 > 50 && paybackPeriod && paybackPeriod < 3) {
    return 'APPROVE'; // Good ROI
  } else if (rosiYear1 > 0 && year5NPV > 0) {
    return 'CONSIDER'; // Positive but modest
  } else if (rosiYear1 < -50 || year5NPV < -100000) {
    return 'REJECT'; // Very poor ROI
  } else {
    return 'DEFER'; // Consider other priorities
  }
}

// ============================================================================
// COMPARISON ENGINE
// ============================================================================

export function compareInvestments(
  investments: SecurityInvestment[],
  riskProfile: RiskProfile
): ComparisonResult {
  const results = investments.map(inv => calculateROSI(inv, riskProfile));

  // Rank by 5-year NPV
  const ranking: InvestmentRanking[] = results
    .map((result) => ({
      rank: 0, // Will be set below
      investmentName: result.investmentName,
      rosi5Year: result.rosi5Year,
      npv5Year: result.npv5Year,
      paybackPeriod: result.paybackPeriod,
      score: result.npv5Year // Use NPV as primary ranking metric
    }))
    .sort((a, b) => b.score - a.score)
    .map((item, index) => ({ ...item, rank: index + 1 }));

  // Combined analysis (if deploying all)
  let combinedAnalysis: CombinedInvestmentAnalysis | undefined;

  if (investments.length > 1) {
    const totalCostYear1 = results.reduce((sum, r) => sum + r.costs.year1Total, 0);
    const totalAnnualCost = results.reduce((sum, r) => sum + r.costs.annual, 0);
    const combinedRiskReduction = Math.min(
      results.reduce((sum, r) => sum + (r.benefits.riskReductionAnnual / riskProfile.ALE) * 100, 0),
      95 // Cap at 95% (can't eliminate all risk)
    );

    // Calculate combined NPV (simple sum - could be more sophisticated)
    const combinedNPV = results.reduce((sum, r) => sum + r.npv5Year, 0);
    const combinedBenefit = results.reduce((sum, r) => sum + r.benefits.riskReductionAnnual, 0);
    const combinedROSI = ((combinedBenefit - totalAnnualCost) / totalCostYear1) * 100;

    // Optimal deployment order (by payback period)
    const optimalOrder = results
      .filter(r => r.paybackPeriod !== null)
      .sort((a, b) => (a.paybackPeriod || 999) - (b.paybackPeriod || 999))
      .map(r => r.investmentName);

    combinedAnalysis = {
      totalCostYear1,
      totalAnnualCost,
      combinedRiskReduction,
      combinedROSI,
      combinedNPV,
      optimalDeploymentOrder: optimalOrder
    };
  }

  return {
    investments: results,
    ranking,
    combinedAnalysis
  };
}
